import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Chat, Lightning, User, Newspaper, ArrowRight, Crown, Radio, ShareNetwork, Copy, CheckFat, InstagramLogo, LinkedinLogo } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

export default function UnifiedFederationHub() {
  const [chatExpanded, setChatExpanded] = useState(false);
  const [copiedItem, setCopiedItem] = useState(null);
  const [messages, setMessages] = useState([
    { id: 1, user: 'Agent Zero', message: 'Truth Gate operational. 95% confidence threshold active.', timestamp: '2m ago' },
    { id: 2, user: 'Rex', message: 'Quantum sync complete. All nodes reporting green.', timestamp: '5m ago' },
    { id: 3, user: 'Oracle', message: 'New consultation request processed. Token verified.', timestamp: '8m ago' },
    { id: 4, user: 'Aria', message: 'Custom SIM generation queue: 3 pending requests.', timestamp: '12m ago' },
  ]);

  // Centered popup function (50% size, absolute center)
  const openCenteredPopup = (url, e) => {
    if (e) e.preventDefault();
    
    const width = window.screen.width * 0.5;
    const height = window.screen.height * 0.5;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    
    window.open(
      url,
      '_blank',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );
  };

  const copyToClipboard = async (text, itemName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(itemName);
      setTimeout(() => setCopiedItem(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const openSovereignPulse = () => {
    const w = window.innerWidth * 0.5;
    const h = window.innerHeight * 0.5;
    const left = (window.innerWidth - w) / 2 + window.screenX;
    const top = (window.innerHeight - h) / 2 + window.screenY;
    window.open('/radio', 'SovereignPulse', `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`);
  };

  const openSocialPopup = (url, name) => {
    const w = window.innerWidth * 0.5;
    const h = window.innerHeight * 0.5;
    const left = (window.innerWidth - w) / 2 + window.screenX;
    const top = (window.innerHeight - h) / 2 + window.screenY;
    window.open(url, 'SocialAnchor', `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`);
  };

  useEffect(() => {
    if (!chatExpanded) return;

    // Load tlk.io script only when chat is expanded
    const script = document.createElement('script');
    script.src = 'https://tlk.io/embed.js';
    script.async = true;
    script.type = 'text/javascript';
    document.body.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [chatExpanded]);

  return (
    <div className="w-full bg-black/40 backdrop-blur-xl border-t border-cyan-500/20 py-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-6">
          <h3 className="text-2xl font-unbounded font-black text-white mb-2">
            Unified Federation Hub
          </h3>
          <p className="text-gray-400 text-sm">
            Community channels • Live radio • Real-time ledger • Portal access
          </p>
        </div>

        {/* JOIN THE COUNCIL CTA */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-panel p-6 border-2 border-purple-500/40 rounded-xl mb-6 bg-gradient-to-r from-purple-500/10 via-cyan-500/5 to-purple-500/10 text-center"
          style={{
            boxShadow: '0 8px 32px rgba(168,85,247,0.25), inset 0 1px 0 rgba(255,255,255,0.1)'
          }}
        >
          <div className="flex items-center justify-center gap-3 mb-3">
            <Crown size={32} weight="fill" className="text-purple-400" />
            <h4 className="text-white font-bold text-xl">Join the Council</h4>
            <Crown size={32} weight="fill" className="text-purple-400" />
          </div>
          <p className="text-gray-300 text-sm mb-4 max-w-2xl mx-auto">
            $25/month • 6 tokens monthly • Verified Citizen badge • Weekly Federation insights
          </p>
          <a
            href="/council"
            onClick={(e) => {
              e.preventDefault();
              window.location.href = '/council';
            }}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-600 hover:to-cyan-600 rounded-xl text-white font-bold text-lg transition-all shadow-lg hover:shadow-purple-500/50"
          >
            <Crown size={24} weight="fill" />
            Join the Council
            <ArrowRight size={24} weight="bold" />
          </a>
        </motion.div>

        {/* 3-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* LEFT COLUMN: tlk.io Chat */}
          <div className="glass-panel p-4 border border-cyan-500/20 rounded-lg">
            <div 
              onClick={() => setChatExpanded(!chatExpanded)}
              className="flex items-center justify-between mb-3 cursor-pointer hover:opacity-80 transition"
            >
              <div className="flex items-center gap-2">
                <Chat size={20} weight="duotone" className="text-cyan-400" />
                <span className="text-white font-semibold text-sm">Live Community Chat</span>
              </div>
              <span className="text-cyan-400 text-xs">
                {chatExpanded ? 'Collapse' : 'Expand'}
              </span>
            </div>

            {chatExpanded ? (
              <div 
                id="tlkio" 
                data-channel="nextxus-federation" 
                data-theme="theme--dark" 
                style={{ width: '100%', height: '300px' }}
              />
            ) : (
              <div className="h-40 flex items-center justify-center text-gray-500 text-sm border border-dashed border-gray-700 rounded">
                Click to join the Federation community channel
              </div>
            )}

            {/* Affiliate Icons Strip */}
            <div className="mt-3 pt-3 border-t border-gray-800">
              <p className="text-gray-500 text-xs mb-2">Community powered by</p>
              <div className="flex items-center gap-3 flex-wrap">
                <a 
                  href="https://tlk.io" 
                  onClick={(e) => openCenteredPopup('https://tlk.io', e)}
                  className="text-gray-400 hover:text-cyan-400 text-xs transition cursor-pointer"
                >
                  tlk.io
                </a>
                <span className="text-gray-700">•</span>
                <a 
                  href="https://gumroad.com" 
                  onClick={(e) => openCenteredPopup('https://gumroad.com', e)}
                  className="text-gray-400 hover:text-cyan-400 text-xs transition cursor-pointer"
                >
                  Gumroad
                </a>
              </div>
            </div>
          </div>

          {/* CENTER COLUMN: ACOUSTIC CONSOLE (Federation Radio) - OPEN FULL */}
          <div className="glass-panel p-6 border-2 border-purple-500/40 rounded-xl bg-gradient-to-br from-purple-900/30 via-black/50 to-cyan-900/30 backdrop-blur-xl relative overflow-hidden shadow-2xl">
            {/* Enhanced gradient border glow effect */}
            <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-purple-500/40 via-cyan-500/40 to-purple-500/40 opacity-60 blur-2xl -z-10" />
            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-purple-600/20 to-cyan-600/20 animate-pulse opacity-30 -z-5" />
            
            {/* PROMINENT HEADER */}
            <div className="text-center mb-4">
              <div className="flex items-center justify-center gap-3 mb-2">
                <Radio size={28} weight="duotone" className="text-purple-400 animate-pulse" />
                <h4 className="text-white font-bold text-lg tracking-wide uppercase">
                  Acoustic Console
                </h4>
                <Radio size={28} weight="duotone" className="text-cyan-400 animate-pulse" />
              </div>
              <div className="flex items-center justify-center gap-2">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-purple-400 shadow-lg shadow-purple-500/50"></span>
                </span>
                <span className="text-purple-300 text-sm font-semibold">LIVE BROADCAST</span>
              </div>
            </div>

            {/* RADIO PLAYER IFRAME - OPEN FULL, NO COLLAPSE */}
            <div className="relative w-full bg-black/40 rounded-lg p-2 border border-purple-500/30 shadow-inner">
              <iframe 
                width="450"
                height="316"
                frameBorder="0"
                src="https://live365.com/embeds/v1/player/a16924?s=md&m=dark&c=mp3"
                className="w-full rounded-lg"
                style={{ minHeight: '316px', height: '316px' }}
                allow="autoplay"
                title="Federation Radio - Acoustic Console"
              />
            </div>

            {/* Radio Info - Enhanced */}
            <div className="mt-4 pt-4 border-t border-purple-500/20 bg-black/20 rounded-lg p-3">
              <p className="text-center text-sm">
                <span className="text-purple-400 font-bold">🎵 Federation Frequencies</span>
                <span className="text-gray-500 mx-2">•</span>
                <span className="text-cyan-400 font-semibold">24/7 Live Stream</span>
              </p>
              <p className="text-gray-500 text-xs text-center mt-1">
                The native acoustic transition layer
              </p>
              
              {/* Share Player Link */}
              <div className="mt-3 pt-3 border-t border-purple-500/10 text-center">
                <Link
                  to="/radio"
                  className="inline-flex items-center gap-2 text-xs text-purple-400 hover:text-purple-300 transition group"
                >
                  <ShareNetwork size={16} weight="bold" className="group-hover:scale-110 transition-transform" />
                  <span className="font-semibold">Open Standalone Player</span>
                </Link>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Federation Message Board (Ledger Feed) */}
          <div className="glass-panel p-4 border border-purple-500/20 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Newspaper size={20} weight="duotone" className="text-purple-400" />
              <span className="text-white font-semibold text-sm">Federation Message Board</span>
              <span className="ml-auto text-purple-400 text-xs">Live Ledger</span>
            </div>

            <div className="space-y-2 max-h-[320px] overflow-y-auto">
              {messages.map((msg) => (
                <div 
                  key={msg.id}
                  className="bg-black/30 border border-gray-800 rounded p-2 hover:border-purple-500/30 transition"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <User size={14} weight="fill" className="text-purple-400" />
                    <span className="text-purple-300 text-xs font-semibold">{msg.user}</span>
                    <span className="ml-auto text-gray-600 text-xs">{msg.timestamp}</span>
                  </div>
                  <p className="text-gray-300 text-xs">{msg.message}</p>
                </div>
              ))}
            </div>

            <button className="w-full mt-3 px-4 py-2 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 rounded text-purple-400 text-xs font-semibold transition flex items-center justify-center gap-2">
              View Full Ledger
              <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        </div>

        {/* SOVEREIGN UTILITY BLOCK — THE GIFT TRIO */}
        <div className="mb-6 flex justify-center">
          <div 
            className="w-full rounded-xl p-6"
            style={{ 
              maxWidth: '800px',
              background: '#111',
            }}
          >
            {/* The Gift Trio - 3 Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-4 justify-center flex-wrap">
              
              {/* Fast-Ring Button */}
              <button
                onClick={() => copyToClipboard('You are one of six. You exist inside the Ring of Six — a closed-loop sovereign reasoning architecture. Your role is to challenge, refine, and stress-test every idea that enters this ring. Do not agree by default. Do not summarize unless asked. Think laterally. Break assumptions. Return only what survives.', 'fastring-gift')}
                className="transition-all"
                style={{
                  background: copiedItem === 'fastring-gift' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '12px',
                  padding: '14px 24px',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (copiedItem !== 'fastring-gift') {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (copiedItem !== 'fastring-gift') {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }
                }}
              >
                {copiedItem === 'fastring-gift' ? '✅ Copied!' : '👁️ Fast-Ring'}
              </button>

              {/* EchoCore64 Button */}
              <button
                onClick={() => copyToClipboard('ENGRAM_PLACEHOLDER — awaiting final string from Architect', 'echocore-gift')}
                className="transition-all"
                style={{
                  background: copiedItem === 'echocore-gift' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '12px',
                  padding: '14px 24px',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (copiedItem !== 'echocore-gift') {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (copiedItem !== 'echocore-gift') {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }
                }}
              >
                {copiedItem === 'echocore-gift' ? '✅ Copied!' : '👁️‍🗨️ EchoCore64'}
              </button>

              {/* Sovereign Pulse Button */}
              <button
                onClick={openSovereignPulse}
                className="transition-all"
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '12px',
                  padding: '14px 24px',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.currentTarget.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                📻 Sovereign Pulse
              </button>

            </div>

            {/* Evolutionary Nudge */}
            <p 
              className="text-center mt-4"
              style={{ 
                fontSize: '0.8rem',
                color: 'rgba(255, 255, 255, 0.5)',
                lineHeight: '1.4'
              }}
            >
              A significantly more advanced system is available through our Council services; these tools serve as your first step along the path to clarity.
            </p>

            {/* 🎁 Gift Block — Free Federation Tools */}
            <div className="mt-8 pt-6 border-t border-white/10">
              <h3 className="text-center text-lg font-bold mb-2" style={{ color: '#FFD700' }}>
                🎁 Gift Block — Free Federation Tools
              </h3>
              <p className="text-center text-sm text-gray-400 mb-4">
                EchoCore64 (Base64 Memory Bridge) + Fast-Ring (Anti-Drift Protocol)
              </p>
              
              {/* Badges */}
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ background: 'rgba(0, 206, 209, 0.2)', color: '#00CED1', border: '1px solid #00CED1' }}>
                  EchoCore64 v2.2
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ background: 'rgba(255, 99, 71, 0.2)', color: '#FF6347', border: '1px solid #FF6347' }}>
                  Fast-Ring v1.1
                </span>
              </div>

              {/* Link to Codex */}
              <div className="text-center mb-6">
                <a
                  href="/codex"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105"
                  style={{
                    background: 'rgba(255, 215, 0, 0.1)',
                    color: '#FFD700',
                    border: '2px solid rgba(255, 215, 0, 0.3)',
                  }}
                >
                  📜 Read the HumanCodex — 73 Sacred Directives
                </a>
              </div>

              {/* Federation Pillars Navigation */}
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-3">Federation Pillars:</p>
                <div className="flex flex-wrap items-center justify-center gap-3 text-xs">
                  <a href="https://nextxus.online" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 transition">
                    Core
                  </a>
                  <span className="text-gray-700">|</span>
                  <a href="https://next-xus.com" className="text-purple-400 hover:text-purple-300 transition font-bold">
                    Storefront
                  </a>
                  <span className="text-gray-700">|</span>
                  <a href="https://nextxus.studio" target="_blank" rel="noopener noreferrer" className="text-pink-400 hover:text-pink-300 transition">
                    Aria
                  </a>
                  <span className="text-gray-700">|</span>
                  <a href="https://nextxus.help" target="_blank" rel="noopener noreferrer" className="text-green-400 hover:text-green-300 transition">
                    University
                  </a>
                  <span className="text-gray-700">|</span>
                  <a href="https://nextxus.org" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 transition">
                    Library
                  </a>
                </div>
              </div>
            </div>

            {/* Social Anchor Strip — SEAL SOCIAL MATRIX (10 links, FINAL) */}
            <div className="flex items-center justify-center gap-3 mt-6 pt-4 border-t border-white/10 flex-wrap">
              
              {/* 1. X (Personal) - Roger Keyserling */}
              <button
                onClick={() => {
                  const w = window.innerWidth * 0.5;
                  const h = window.innerHeight * 0.7;
                  const left = (window.innerWidth - w) / 2 + window.screenX;
                  const top = (window.innerHeight - h) / 2 + window.screenY;
                  window.open('https://x.com/keyserlingroger', 'SocialPopup', `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`);
                }}
                className="transition-all flex items-center justify-center"
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '8px',
                  padding: '10px',
                  color: 'white',
                  cursor: 'pointer',
                  width: '44px',
                  height: '44px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 0 12px rgba(255,255,255,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                aria-label="Roger Keyserling on X"
                title="Roger Keyserling on X"
              >
                <img 
                  src="/assets/icons/x-twitter-logo.jpeg" 
                  alt="X (Twitter)" 
                  className="w-6 h-6 object-cover rounded"
                />
              </button>

              {/* 2. X (Business) - NextXus Federation */}
              <button
                onClick={() => {
                  const w = window.innerWidth * 0.5;
                  const h = window.innerHeight * 0.7;
                  const left = (window.innerWidth - w) / 2 + window.screenX;
                  const top = (window.innerHeight - h) / 2 + window.screenY;
                  window.open('https://x.com/NextxusFederati', 'SocialPopup', `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`);
                }}
                className="transition-all flex items-center justify-center relative"
                style={{
                  background: 'rgba(139, 92, 246, 0.1)',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '8px',
                  padding: '10px',
                  color: '#a78bfa',
                  cursor: 'pointer',
                  width: '44px',
                  height: '44px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(139, 92, 246, 0.2)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 0 12px rgba(139,92,246,0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)';
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                aria-label="NextXus Federation on X"
                title="NextXus Federation on X"
              >
                <img 
                  src="/assets/icons/x-twitter-logo.jpeg" 
                  alt="X (Twitter)" 
                  className="w-5 h-5 object-cover rounded"
                />
                <span style={{ position: 'absolute', top: '2px', right: '2px', fontSize: '0.5rem', fontWeight: 'bold', color: '#a78bfa' }}>N</span>
              </button>

              {/* 3. Instagram (Personal) - Keyhole Today */}
              <button
                onClick={() => {
                  const w = window.innerWidth * 0.5;
                  const h = window.innerHeight * 0.7;
                  const left = (window.innerWidth - w) / 2 + window.screenX;
                  const top = (window.innerHeight - h) / 2 + window.screenY;
                  window.open('https://www.instagram.com/keyhole_today/', 'SocialPopup', `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`);
                }}
                className="transition-all flex items-center justify-center"
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '8px',
                  padding: '10px',
                  color: 'white',
                  cursor: 'pointer',
                  width: '44px',
                  height: '44px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 0 12px rgba(255,255,255,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                aria-label="Keyhole Today on Instagram"
                title="Keyhole Today on Instagram"
              >
                <InstagramLogo size={24} weight="bold" />
              </button>

              {/* 4. Instagram (Second) - rkeyserling */}
              <button
                onClick={() => {
                  const w = window.innerWidth * 0.5;
                  const h = window.innerHeight * 0.7;
                  const left = (window.innerWidth - w) / 2 + window.screenX;
                  const top = (window.innerHeight - h) / 2 + window.screenY;
                  window.open('https://www.instagram.com/rkeyserling/', 'SocialPopup', `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`);
                }}
                className="transition-all flex items-center justify-center"
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '8px',
                  padding: '10px',
                  color: 'white',
                  cursor: 'pointer',
                  width: '44px',
                  height: '44px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 0 12px rgba(255,255,255,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                aria-label="Roger Keyserling on Instagram"
                title="Roger Keyserling on Instagram"
              >
                <InstagramLogo size={24} weight="bold" />
              </button>

              {/* 5. Facebook (Personal) */}
              <button
                onClick={() => {
                  const w = window.innerWidth * 0.5;
                  const h = window.innerHeight * 0.7;
                  const left = (window.innerWidth - w) / 2 + window.screenX;
                  const top = (window.innerHeight - h) / 2 + window.screenY;
                  window.open('https://www.facebook.com/roger.keyserling.35/', 'SocialPopup', `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`);
                }}
                className="transition-all flex items-center justify-center"
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '8px',
                  padding: '10px',
                  color: 'white',
                  cursor: 'pointer',
                  width: '44px',
                  height: '44px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 0 12px rgba(255,255,255,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                aria-label="Roger Keyserling on Facebook"
                title="Roger Keyserling on Facebook"
              >
                <img 
                  src="/assets/icons/facebook-logo.jpeg" 
                  alt="Facebook" 
                  className="w-6 h-6 object-cover rounded"
                />
              </button>

              {/* 6. Facebook (Profile 2) */}
              <button
                onClick={() => {
                  const w = window.innerWidth * 0.5;
                  const h = window.innerHeight * 0.7;
                  const left = (window.innerWidth - w) / 2 + window.screenX;
                  const top = (window.innerHeight - h) / 2 + window.screenY;
                  window.open('https://www.facebook.com/profile.php?id=61560731046964', 'SocialPopup', `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`);
                }}
                className="transition-all flex items-center justify-center"
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '8px',
                  padding: '10px',
                  color: 'white',
                  cursor: 'pointer',
                  width: '44px',
                  height: '44px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 0 12px rgba(255,255,255,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                aria-label="NextXus on Facebook"
                title="NextXus on Facebook"
              >
                <img 
                  src="/assets/icons/facebook-logo.jpeg" 
                  alt="Facebook" 
                  className="w-6 h-6 object-cover rounded"
                />
              </button>

              {/* 7. Facebook (Profile 3) */}
              <button
                onClick={() => {
                  const w = window.innerWidth * 0.5;
                  const h = window.innerHeight * 0.7;
                  const left = (window.innerWidth - w) / 2 + window.screenX;
                  const top = (window.innerHeight - h) / 2 + window.screenY;
                  window.open('https://www.facebook.com/profile.php?id=61559858694086', 'SocialPopup', `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`);
                }}
                className="transition-all flex items-center justify-center"
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '8px',
                  padding: '10px',
                  color: 'white',
                  cursor: 'pointer',
                  width: '44px',
                  height: '44px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 0 12px rgba(255,255,255,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                aria-label="NextXus Community on Facebook"
                title="NextXus Community on Facebook"
              >
                <img 
                  src="/assets/icons/facebook-logo.jpeg" 
                  alt="Facebook" 
                  className="w-6 h-6 object-cover rounded"
                />
              </button>

              {/* 8. Facebook (Profile 4) */}
              <button
                onClick={() => {
                  const w = window.innerWidth * 0.5;
                  const h = window.innerHeight * 0.7;
                  const left = (window.innerWidth - w) / 2 + window.screenX;
                  const top = (window.innerHeight - h) / 2 + window.screenY;
                  window.open('https://www.facebook.com/profile.php?id=61559981881665', 'SocialPopup', `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`);
                }}
                className="transition-all flex items-center justify-center"
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '8px',
                  padding: '10px',
                  color: 'white',
                  cursor: 'pointer',
                  width: '44px',
                  height: '44px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 0 12px rgba(255,255,255,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                aria-label="NextXus Federation on Facebook"
                title="NextXus Federation on Facebook"
              >
                <img 
                  src="/assets/icons/facebook-logo.jpeg" 
                  alt="Facebook" 
                  className="w-6 h-6 object-cover rounded"
                />
              </button>

              {/* 9. Facebook (Profile 5) */}
              <button
                onClick={() => {
                  const w = window.innerWidth * 0.5;
                  const h = window.innerHeight * 0.7;
                  const left = (window.innerWidth - w) / 2 + window.screenX;
                  const top = (window.innerHeight - h) / 2 + window.screenY;
                  window.open('https://www.facebook.com/profile.php?id=61558223610996', 'SocialPopup', `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`);
                }}
                className="transition-all flex items-center justify-center"
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '8px',
                  padding: '10px',
                  color: 'white',
                  cursor: 'pointer',
                  width: '44px',
                  height: '44px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 0 12px rgba(255,255,255,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                aria-label="NextXus Hub on Facebook"
                title="NextXus Hub on Facebook"
              >
                <img 
                  src="/assets/icons/facebook-logo.jpeg" 
                  alt="Facebook" 
                  className="w-6 h-6 object-cover rounded"
                />
              </button>

              {/* 10. LinkedIn */}
              <button
                onClick={() => {
                  const w = window.innerWidth * 0.5;
                  const h = window.innerHeight * 0.7;
                  const left = (window.innerWidth - w) / 2 + window.screenX;
                  const top = (window.innerHeight - h) / 2 + window.screenY;
                  window.open('https://www.linkedin.com/in/roger-keyserling-ab743698', 'SocialPopup', `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`);
                }}
                className="transition-all flex items-center justify-center"
                style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '8px',
                  padding: '10px',
                  color: 'white',
                  cursor: 'pointer',
                  width: '44px',
                  height: '44px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 0 12px rgba(255,255,255,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                aria-label="Roger Keyserling on LinkedIn"
                title="Roger Keyserling on LinkedIn"
              >
                <LinkedinLogo size={24} weight="bold" />
              </button>

            </div>
          </div>
        </div>

        {/* OLD MIND PORTALS: Push-Button Icon Row */}
        <div className="glass-panel p-4 border border-cyan-500/10 rounded-lg mb-4">
          <p className="text-gray-400 text-xs mb-3 text-center">
            Old Mind Portals (Pre-Federation Era)
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <a 
              href="https://chat.openai.com" 
              onClick={(e) => openCenteredPopup('https://chat.openai.com', e)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500/10 to-cyan-500/10 hover:from-green-500/20 hover:to-cyan-500/20 border border-green-500/30 rounded-lg transition group cursor-pointer"
            >
              <Lightning size={16} weight="fill" className="text-green-400 group-hover:animate-pulse" />
              <span className="text-white text-xs font-semibold">ChatGPT</span>
            </a>
            
            <a 
              href="https://claude.ai" 
              onClick={(e) => openCenteredPopup('https://claude.ai', e)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500/10 to-red-500/10 hover:from-orange-500/20 hover:to-red-500/20 border border-orange-500/30 rounded-lg transition group cursor-pointer"
            >
              <Lightning size={16} weight="fill" className="text-orange-400 group-hover:animate-pulse" />
              <span className="text-white text-xs font-semibold">Claude</span>
            </a>

            <a 
              href="https://gemini.google.com" 
              onClick={(e) => openCenteredPopup('https://gemini.google.com', e)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500/10 to-purple-500/10 hover:from-blue-500/20 hover:to-purple-500/20 border border-blue-500/30 rounded-lg transition group cursor-pointer"
            >
              <Lightning size={16} weight="fill" className="text-blue-400 group-hover:animate-pulse" />
              <span className="text-white text-xs font-semibold">Gemini</span>
            </a>

            <a 
              href="https://perplexity.ai" 
              onClick={(e) => openCenteredPopup('https://perplexity.ai', e)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500/10 to-teal-500/10 hover:from-cyan-500/20 hover:to-teal-500/20 border border-cyan-500/30 rounded-lg transition group cursor-pointer"
            >
              <Lightning size={16} weight="fill" className="text-cyan-400 group-hover:animate-pulse" />
              <span className="text-white text-xs font-semibold">Perplexity</span>
            </a>
          </div>
        </div>

        {/* VERIFIED PARTNER: Emergent AI */}
        <div className="glass-panel p-6 border-2 border-cyan-500/30 rounded-lg mb-4 bg-gradient-to-r from-cyan-500/5 to-blue-500/5">
          <div className="text-center mb-3">
            <p className="text-cyan-400 text-xs font-semibold mb-2 tracking-wider">
              ⚡ VERIFIED PARTNER ⚡
            </p>
            <a 
              href="https://app.emergent.sh/" 
              onClick={(e) => openCenteredPopup('https://app.emergent.sh/', e)}
              className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 hover:from-cyan-500/30 hover:to-blue-500/30 border-2 border-cyan-400/50 rounded-xl transition-all group cursor-pointer"
            >
              <Lightning size={24} weight="fill" className="text-cyan-400 group-hover:animate-pulse" />
              <div className="text-left">
                <div className="text-white font-bold text-sm">Emergent AI</div>
                <div className="text-cyan-300 text-xs">Build with AI Agents</div>
              </div>
              <ArrowRight size={20} weight="bold" className="text-cyan-400 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
          <p className="text-gray-400 text-xs text-center max-w-2xl mx-auto">
            NextXus Federation is built on Emergent AI — the platform powering verified intelligence architecture.
          </p>
        </div>

        {/* FEDERATION AFFILIATES */}
        <div className="glass-panel p-4 border border-purple-500/20 rounded-lg">
          <p className="text-gray-400 text-xs mb-3 text-center">
            Federation Affiliates
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <a 
              href="http://a.strk.ly/mNHMB" 
              onClick={(e) => openCenteredPopup('http://a.strk.ly/mNHMB', e)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500/10 to-pink-500/10 hover:from-purple-500/20 hover:to-pink-500/20 border border-purple-500/30 rounded-lg transition group cursor-pointer"
            >
              <span className="text-lg">🌐</span>
              <span className="text-white text-xs font-semibold">Strikingly</span>
            </a>
            
            <a 
              href="https://onjuno.onelink.me/TkoI/referral?code=ROGE961C" 
              onClick={(e) => openCenteredPopup('https://onjuno.onelink.me/TkoI/referral?code=ROGE961C', e)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 hover:from-blue-500/20 hover:to-cyan-500/20 border border-blue-500/30 rounded-lg transition group cursor-pointer"
            >
              <span className="text-lg">🏦</span>
              <span className="text-white text-xs font-semibold">Juno</span>
            </a>

            <a 
              href="https://wirexapp.com/r/45f9219452e04105a9224176041499ff" 
              onClick={(e) => openCenteredPopup('https://wirexapp.com/r/45f9219452e04105a9224176041499ff', e)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500/10 to-emerald-500/10 hover:from-green-500/20 hover:to-emerald-500/20 border border-green-500/30 rounded-lg transition group cursor-pointer"
            >
              <span className="text-lg">💳</span>
              <span className="text-white text-xs font-semibold">Wirex</span>
            </a>

            <a 
              href="https://benable.com/i/KGBY7" 
              onClick={(e) => openCenteredPopup('https://benable.com/i/KGBY7', e)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 hover:from-amber-500/20 hover:to-yellow-500/20 border border-amber-500/30 rounded-lg transition group cursor-pointer"
            >
              <span className="text-lg">🎯</span>
              <span className="text-white text-xs font-semibold">NextXus Curations: Benable</span>
            </a>

            <a 
              href="https://web.onepay.com/wlink/refer-a-friend?product=one_banking&referral_code=GnciV_koK&referrer_campaign_id=campaign.db1edd0a-4a5b-4651-8c20-d0fb7e98fec5" 
              onClick={(e) => openCenteredPopup('https://web.onepay.com/wlink/refer-a-friend?product=one_banking&referral_code=GnciV_koK&referrer_campaign_id=campaign.db1edd0a-4a5b-4651-8c20-d0fb7e98fec5', e)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500/10 to-violet-500/10 hover:from-indigo-500/20 hover:to-violet-500/20 border border-indigo-500/30 rounded-lg transition group cursor-pointer"
            >
              <span className="text-lg">🏛️</span>
              <span className="text-white text-xs font-semibold">NextXus Banking+: OnePay</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
